import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/components/useLanguage';
import PortalHeader from '@/components/PortalHeader';
import PortalHelpLinks from '@/components/support/PortalHelpLinks';
import HenvisningBanner from '@/components/HenvisningBanner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Kundeportal() {
  const { lang, t } = useLanguage();
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isLeavingRef = useRef(false);
  const previousPageRef = useRef('/supportkundeportal?customer=1');

  // React Router state records the real in-app origin; referrer does not update on SPA navigation.
  // Never trap browser Back or overwrite the router's own history state.
  useEffect(() => {
    const previous = location.state?.journeyFrom;
    if (typeof previous === 'string' && previous.startsWith('/') && !previous.startsWith('//') && !/^\/(?:en\/customer-portal|kundeportal)(?:[/?#]|$)/i.test(previous)) previousPageRef.current = previous;
  }, [location.state]);

  const handleLeavePortal = (e) => {
    if (e) e.preventDefault();
    setShowLogoutWarning(true);
  };

  const confirmLeave = () => {
    isLeavingRef.current = true;
    setShowLogoutWarning(false);
    navigate(previousPageRef.current, { replace: true });
  };

  return (
    <div className="customer-portal-shell min-h-screen flex flex-col">
      {/* Minimal Portal Header */}
      <PortalHeader onLeaveClick={handleLeavePortal} />
      <PortalHelpLinks />

      {/* Henvisning Banner */}
      <HenvisningBanner variant="light" />

      {/* English notice - thin bar */}
      {lang === 'en' && (
        <div className="portal-language-notice border-b px-4 py-2 text-center">
          <p className="text-sm text-amber-800">
            {t('customerPortal.danishOnlyNotice')}
          </p>
        </div>
      )}

      {/* Iframe Section - Full remaining height */}
      <div className="flex-1">
        <iframe
          src="https://enkelenergi.min-forsyning.dk/login"
          title={t('customerPortal.title')}
          className="w-full h-full border-0"
          style={{ minHeight: `calc(100vh - 88px - 52px${lang === 'en' ? ' - 40px' : ''})` }}
          allow="clipboard-write"
        />
      </div>

      {/* Compact Footer */}
      <footer className="bg-[#1F2E33] text-gray-300">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Quick links */}
            <nav className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <button 
                onClick={handleLeavePortal}
                className="hover:text-[#D4A62A] transition-colors font-medium"
              >
                {t('customerPortal.leavePortal')}
              </button>
              <span className="text-gray-600">•</span>
              <a href="mailto:hej@enkelenergi.dk" className="hover:text-[#D4A62A] transition-colors">
                {t('kontakt.email')}
              </a>
              <span className="text-gray-600">•</span>
              <a href="tel:+4570608007" className="hover:text-[#D4A62A] transition-colors">
                {t('kontakt.phoneShort')}
              </a>
            </nav>

            {/* Copyright */}
            <p className="text-xs text-gray-500">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </footer>

      {/* Logout Warning Dialog */}
      <AlertDialog open={showLogoutWarning} onOpenChange={setShowLogoutWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('customerPortal.leaveWarningTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('customerPortal.leaveWarningMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLeave}>
              {t('customerPortal.leavePortal')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}